import React, { useState } from 'react';
import {
  criterionOptionsFor,
  factorGroupsFor,
  ROLE_DEFAULT_SOURCE,
  TAG_KIND,
  TAG_KIND_OPTIONS,
  TAG_NOTE_MAX_CHARS,
  tagSourceLabel,
} from '../../constants/evidenceTagging';

/**
 * EvidenceTagForm
 * ---------------
 * The choices behind every Tag button (TDI-394, Tom King's ruling R1): which
 * part of the answer, what it is (Criterion, Risk, Other factor or Add to my
 * conclusion), the criterion or factor group it belongs to, and an optional
 * note. Shared by the history review's category Tag button and the decision
 * screens' referral-evidence panel, so tagging reads the same everywhere.
 *
 * The clinician makes every choice. Nothing is pre-selected apart from the
 * first part of the answer, and where the evidence came from follows from
 * whose answer it is (TDI-393).
 *
 * Props:
 *   - pathway: 'adhd' | 'asd' — decides the criteria and factor groups
 *   - targets: [{ formKey, role, questionId, label, values: string[] }] — the
 *     answers this Tag button covers; a category answered by both the school
 *     and the parent or carer has one target for each
 *   - onSubmit(tag): async; resolves once the tag is saved, or to false when
 *     the save failed (the form then stays open with the choices kept)
 *   - onClose()
 */

/** Truncate a line for a select option. */
const shortLine = (text, max = 60) =>
  (text || '').length > max ? `${(text || '').slice(0, max - 1)}…` : (text || '');

const SELECT_CLASS =
  'mt-1 block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-sm font-normal text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary';

/**
 * Every line a tag can point at across the targets. A target with several
 * lines offers "The whole answer" first, then each line; a single-line target
 * offers its one line. With more than one target each option says whose
 * answer it is.
 */
export function tagPartOptions(targets) {
  const options = [];
  (targets || []).forEach((target, targetIndex) => {
    const values = target?.values || [];
    if (!values.length) return;
    const prefix = targets.length > 1
      ? `${tagSourceLabel(ROLE_DEFAULT_SOURCE[target.role]) || ''}: `
      : '';
    if (values.length === 1) {
      options.push({ id: `${targetIndex}:all`, targetIndex, partIndex: null, label: `${prefix}${shortLine(values[0])}` });
      return;
    }
    options.push({ id: `${targetIndex}:all`, targetIndex, partIndex: null, label: `${prefix}The whole answer` });
    values.forEach((value, partIndex) => {
      options.push({ id: `${targetIndex}:${partIndex}`, targetIndex, partIndex, label: `${prefix}${shortLine(value)}` });
    });
  });
  return options;
}

export default function EvidenceTagForm({ pathway, targets = [], onSubmit, onClose }) {
  const partOptions = tagPartOptions(targets);
  const [partId, setPartId] = useState(partOptions[0]?.id || '');
  const [kind, setKind] = useState('');
  const [criterionKey, setCriterionKey] = useState('');
  const [factorGroup, setFactorGroup] = useState('');
  const [factorKey, setFactorKey] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const criteria = criterionOptionsFor(pathway);
  const groups = factorGroupsFor(pathway);
  const part = partOptions.find((p) => p.id === partId) || partOptions[0];
  const target = part ? targets[part.targetIndex] : null;
  const source = ROLE_DEFAULT_SOURCE[target?.role] || 'other';
  const chosenGroup = groups.find((g) => g.id === factorGroup);

  const complete = Boolean(target) && (
    (kind === TAG_KIND.CRITERION && Boolean(criterionKey))
    || (kind === TAG_KIND.OTHER_FACTOR && Boolean(factorGroup))
    || kind === TAG_KIND.RISK
    || kind === TAG_KIND.CONCLUSION
  );

  const handleSubmit = async () => {
    if (!complete || busy) return;
    const values = target.values || [];
    setBusy(true);
    try {
      const saved = await onSubmit({
        kind,
        criterionKey: kind === TAG_KIND.CRITERION ? criterionKey : null,
        factorGroup: kind === TAG_KIND.OTHER_FACTOR ? factorGroup : null,
        factorKey: kind === TAG_KIND.OTHER_FACTOR && factorKey ? factorKey : null,
        note: note.trim(),
        source,
        formKey: target.formKey,
        role: target.role,
        questionId: target.questionId,
        label: target.label,
        // A line-level tag records exactly the line it points at; a
        // whole-answer tag keeps the joined answer.
        value: part.partIndex !== null ? values[part.partIndex] : values.join(', '),
        partIndex: part.partIndex,
      });
      // A save the caller reports as failed keeps every choice and the note,
      // so the clinician can try again without retyping.
      if (saved === false) return;
      setKind('');
      setCriterionKey('');
      setFactorGroup('');
      setFactorKey('');
      setNote('');
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="text-left">
      {partOptions.length > 1 && (
        <label className="mt-2 block text-xs font-medium text-gray-600">
          Which part?
          <select value={part?.id || ''} onChange={(e) => setPartId(e.target.value)} className={SELECT_CLASS}>
            {partOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>
      )}

      <fieldset className="mt-2">
        <legend className="text-xs font-medium text-gray-600">What is it?</legend>
        <div className="mt-1 space-y-1">
          {TAG_KIND_OPTIONS.map((option) => (
            <label key={option.id} className="flex items-center gap-2 text-sm text-gray-900">
              <input
                type="radio"
                name={`tag-kind-${targets[0]?.questionId || 'answer'}`}
                value={option.id}
                checked={kind === option.id}
                onChange={() => setKind(option.id)}
                className="h-4 w-4 text-primary focus:ring-primary"
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>

      {kind === TAG_KIND.CRITERION && (
        <label className="mt-2 block text-xs font-medium text-gray-600">
          Which criterion?
          <select value={criterionKey} onChange={(e) => setCriterionKey(e.target.value)} className={SELECT_CLASS}>
            <option value="">Select a criterion</option>
            {criteria.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </label>
      )}

      {kind === TAG_KIND.OTHER_FACTOR && (
        <>
          <label className="mt-2 block text-xs font-medium text-gray-600">
            Group
            <select
              value={factorGroup}
              onChange={(e) => { setFactorGroup(e.target.value); setFactorKey(''); }}
              className={SELECT_CLASS}
            >
              <option value="">Select a group</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.label}</option>
              ))}
            </select>
          </label>
          {chosenGroup && (
            <label className="mt-2 block text-xs font-medium text-gray-600">
              Specific factor (optional)
              <select value={factorKey} onChange={(e) => setFactorKey(e.target.value)} className={SELECT_CLASS}>
                <option value="">Choose a factor, or leave as the group</option>
                {chosenGroup.factors.map((f) => (
                  <option key={f.factorKey} value={f.factorKey}>{f.label}</option>
                ))}
              </select>
            </label>
          )}
        </>
      )}

      <label className="mt-2 block text-xs font-medium text-gray-600">
        Note (optional)
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={TAG_NOTE_MAX_CHARS}
          rows={2}
          className={SELECT_CLASS}
        />
      </label>

      <p className="mt-2 text-xs text-gray-600">
        Evidence from: <span className="font-medium text-gray-900">{tagSourceLabel(source)}</span>
      </p>

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          Close
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!complete || busy}
          className={`rounded-md px-3 py-1.5 text-xs font-medium text-white ${
            complete && !busy ? 'bg-primary hover:bg-primary/90' : 'cursor-not-allowed bg-gray-300'
          }`}
        >
          {busy ? 'Saving…' : 'Add tag'}
        </button>
      </div>
    </div>
  );
}
